import { Toast } from '../../../components/Common/Toast';
import { HTTPStatusEnum } from '../../../interfaces/log.interface';
import { callAPI } from '../../../services/api.service';
import { Dictionary } from '../../../interfaces/_common.interface';
import { ParentChildConfirmationRequest } from '../components/ParentChildConfirmation';
import { NavigateFunction } from 'react-router-dom';

/**
 * Get all students in a classroom
 * @param classroomId Classroom ID
 * @param classroomAssignmentId
 * @param returnStats Optional flag to return stats
 * @returns List of students or null if failed
 */
export const getClassroomProjectStudents = async (
  classroomId: string,
  classroomAssignmentId: string,
  returnStats?: boolean,
): Promise<any> => {
  try {
    const response = await callAPI(
      `api/classrooms/${classroomId}/projects/${classroomAssignmentId}/students${returnStats ? '?returnStats=true' : ''}`,
      null,
      'GET',
    );
    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to fetch students', true);
      return null;
    }
    return response;
  } catch (error) {
    Toast('Failed to fetch students', true);
    return null;
  }
};

/**
 * Get dashboad stats for a classroom Assignment
 * @param classroomId Classroom ID
 * @param classroomAssignmentId Classroom Assignment ID
 * @returns List of students or null if failed
 */
export const getClassroomProjectStats = async (classroomId: string, classroomAssignmentId: string): Promise<any> => {
  try {
    const response = await callAPI(
      `api/classrooms/${classroomId}/projects/${classroomAssignmentId}/students/stats`,
      null,
      'GET',
    );
    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to fetch dashboard data', true);
      return null;
    }
    return response;
  } catch (error) {
    Toast('Failed to fetch dashboard data', true);
    return null;
  }
};

/**
 * Create multiple students at once
 * @param classroomId Classroom ID
 * @param names Array of student names
 * @param skipped
 * @param isOnboarding
 * @param limitException
 * @returns API response or null if failed
 */
export const createStudents = async (
  classroomId: string,
  names: string[],
  skipped: boolean,
  isOnboarding?: boolean,
  limitException?: () => void,
): Promise<any> => {
  try {
    const response = await callAPI(
      `api/classrooms/${classroomId}/students/bulk`,
      { names, ...(skipped ? { skipped } : {}), ...(isOnboarding ? { isOnboarding } : {}) },
      'POST',
    );
    Toast(response?.message || response?.error || 'Unexpected Error Occurred', response?.status !== HTTPStatusEnum.OK);
    if (response?.type === 'student_limit_exceeded' && limitException) {
      limitException();
    }
    return response;
  } catch (error) {
    Toast('Failed to create students', true);
  }
};

/**
 * Complete onboarding
 * @param classroomId Classroom ID
 * @returns API response or null if failed
 */
export const finishOnboarding = async (classroomId: string): Promise<any> => {
  try {
    const response = await callAPI(`api/classrooms/${classroomId}/onboarding/finish`, {}, 'POST');
    return response;
  } catch (error) {
    return null;
  }
};

/**
 * Create a single student
 * @param classroomId Classroom ID
 * @param name Student name
 * @param success Success callback
 * @returns API response or null if failed
 */
export const createStudent = async (classroomId: string, name: string, success?: () => void): Promise<any> => {
  try {
    const response = await callAPI(`api/classrooms/${classroomId}/students`, { name }, 'POST');
    Toast(response?.message || response?.error || 'Unexpected Error Occurred', response?.status !== HTTPStatusEnum.OK);
    if (response?.status === HTTPStatusEnum.OK && success) {
      success();
    }
    return response;
  } catch (error) {
    Toast('Failed to create student', true);
    return null;
  }
};

interface UpdateStudentData {
  studentName?: string;
  bookBalance?: number;
}

/**
 * Update student information
 * @param classroomId Classroom ID
 * @param studentId Student ID (email)
 * @param data Update data
 * @returns Success status
 */
export const updateStudent = async (
  classroomId: string,
  studentId: string,
  data: UpdateStudentData,
): Promise<boolean> => {
  try {
    const response = await callAPI(`api/classrooms/${classroomId}/students/${studentId}`, data, 'PUT');

    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to update student', true);
      return false;
    }

    return true;
  } catch (error) {
    Toast('Failed to update student', true);
    return false;
  }
};

/**
 * Increment student books count
 * @param classroomId Classroom ID
 * @param studentId Student ID (email)
 * @returns Success status
 */
export const incrementStudentBookCount = async (
  classroomId: string,
  studentId: string,
  success?: () => void,
): Promise<boolean> => {
  try {
    const response = await callAPI(`api/classrooms/${classroomId}/students/${studentId}/add-book`, {}, 'POST');

    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to add book credit for student', true);
      return false;
    }
    success && success();
    return true;
  } catch (error) {
    Toast('Failed to add book credit for student', true);
    return false;
  }
};

/**
 * Delete a student
 * @param classroomId Classroom ID
 * @param studentId Student ID (email)
 * @param success Success callback
 * @returns API response or null if failed
 */
export const deleteStudent = async (classroomId: string, studentId: string, success?: () => void): Promise<any> => {
  try {
    const response = await callAPI(`api/classrooms/${classroomId}/students/${studentId}`, null, 'DELETE');
    Toast(response?.message || response?.error || 'Unexpected Error Occurred', response?.status !== HTTPStatusEnum.OK);
    if (response?.status === HTTPStatusEnum.OK && success) {
      success();
    }
    return response;
  } catch (error) {
    Toast('Failed to delete student', true);
    return null;
  }
};

/**
 * Refresh a student's password
 * @param classroomId Classroom ID
 * @param studentId Student ID (email)
 * @param success Success callback
 * @returns API response or null if failed
 */
export const refreshStudentPassword = async (
  classroomId: string,
  studentId: string,
  success?: () => void,
): Promise<any> => {
  try {
    const response = await callAPI(`api/classrooms/${classroomId}/students/${studentId}/refresh-password`, {}, 'POST');
    Toast(response?.message || response?.error || 'Unexpected Error Occurred', response?.status !== HTTPStatusEnum.OK);
    if (response?.status === HTTPStatusEnum.OK && success) {
      success();
    }
    return response;
  } catch (error) {
    Toast('Failed to delete student', true);
    return null;
  }
};

/**
 * Get student password
 * @param classroomId Classroom ID
 * @param studentId Student ID (email)
 * @returns Password or null if failed
 */
export const getStudentPassword = async (classroomId: string, studentId: string): Promise<string | null> => {
  try {
    const response = await callAPI(`api/classrooms/${classroomId}/students/${studentId}/password`, null, 'GET');
    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to fetch password', true);
      return null;
    }
    return response.password;
  } catch (error) {
    Toast('Failed to fetch password', true);
    return null;
  }
};

/**
 * Get student passwords
 * @param classroomId Classroom ID
 * @param projectId
 * @param studentEmails Student IDs (emails)
 * @param printing Optional printing flag
 * @returns Password or null if failed
 */
export const getStudentPasswords = async (
  classroomId: string,
  projectId: string | undefined,
  studentEmails: string[],
  printing?: boolean,
): Promise<Dictionary<string> | null> => {
  try {
    const response = await callAPI(
      `api/classrooms/${classroomId}/students/passwords`,
      { emails: studentEmails, printing, projectId },
      'POST',
    );
    if (response?.status !== HTTPStatusEnum.OK) {
      Toast(response?.error || 'Failed to fetch password', true);
      return null;
    }
    return response.data;
  } catch (error) {
    Toast('Failed to fetch password', true);
    return null;
  }
};
/**
 * Generate pre-session flyers for students
 * @param classroomId Classroom ID
 * @param projectId
 * @param studentIds
 * @returns Password or null if failed
 */
export const generatePreSessionFlyers = async (
  classroomId: string,
  projectId: string,
  studentIds: string[],
): Promise<{ status: number; error?: string }> => {
  const response = await callAPI(
    `api/classrooms/${classroomId}/projects/${projectId}/pre-session-flyers`,
    { studentIds },
    'POST',
  );
  return {
    status: response?.status ?? HTTPStatusEnum.INTERNAL_SERVER_ERROR,
    error: response?.error,
  };
};

/**
 * Confirm VPC child book
 * @param bookId
 * @param shareKey
 * @param data
 * @param dispatch
 * @param navigateTo
 * @returns Password or null if failed
 */
export const confirmChildBook = async (
  bookId: string,
  shareKey: string | null,
  data: ParentChildConfirmationRequest,
  navigateTo: NavigateFunction,
): Promise<void> => {
  try {
    const response = await callAPI(`api/vpc/child-book/${bookId}/confirm`, { ...data }, 'POST', { share: shareKey });
    if (response?.status >= 200 || response?.status < 300) {
      sessionStorage.setItem('showRequireEmailPopup', 'false');
      sessionStorage.setItem('userEmailAddress', data?.email);
      Toast(response.message || 'Confirmed successfully!');
      navigateTo && navigateTo(`/story/bookflip/${bookId}`);
    } else {
      Toast(response?.message || 'Failed to confirm', true);
    }
  } catch (error) {
    Toast('Failed to confirm', true);
  }
};
